import { FC, useRef, useCallback } from "react";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import { useTranslations } from "@jugl-web/utils";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useWebChatBotAccount } from "../../../../providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { DisabledChatInput } from "../DisabledChatInput";
import { useManageAccountsDrawer } from "../../../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

export const WebChatInput: FC<{
  chatId: string;
  conversationName: string;
  onMessageSent?: () => void;
}> = ({ chatId, conversationName, onMessageSent }) => {
  const { sendWebMessage, setIsSendingMessage } = useClientSupportPageContext();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const chatInput$ = useRef<ChatInputHandle>(null);
  const { isConnected } = useWebChatBotAccount();
  const { t } = useTranslations();

  const handleSendMessage: ChatInputParams["onSubmit"] = useCallback(
    async ({ body }) => {
      if (!body.trim()) return;

      try {
        setIsSendingMessage(true);

        sendWebMessage({
          conv_id: chatId,
          conv_name: conversationName,
          text: body,
        });

        setTimeout(() => {
          chatInput$.current?.setValue("");
        }, 100);
        chatInput$.current?.focus();
        onMessageSent?.();
      } catch (error) {
        // NOTE: pass
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      chatId,
      conversationName,
      sendWebMessage,
      onMessageSent,
      setIsSendingMessage,
    ]
  );

  if (!isConnected) {
    return (
      <DisabledChatInput
        text={t({
          id: "client-support-page.web-chat-is-disabled-please-enable",
          defaultMessage:
            "⚠️ Web Chat is disabled. Please enable it to continue using Web Chat CRM",
        })}
        buttonProps={{
          text: t({
            id: "client-support-page.open-settings",
            defaultMessage: "Open Settings",
          }),
          onClick: () => openManageAccountDrawer({ name: "webChatBotAccount" }),
        }}
      />
    );
  }

  return (
    <div className="px-8 pb-1.5 pt-[18px]">
      <ChatInput
        id={chatId}
        ref={chatInput$}
        onSubmit={handleSendMessage}
        isLoading={false}
        toolbarActions={{
          emoji: {
            enabled: true,
          },
          attachments: {
            enabled: false,
          },
        }}
      />
    </div>
  );
};
